import { c as _c } from "react/compiler-runtime";
import AccessDeniedIcon from "@assets/illustrations/access-denied_566px.svg";
import { Box, Stack } from "@mui-wrapper";
import { contactSupport } from "@utils/email-utils";
import { ContactSupportLocations } from "@custom-types/types";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DEFAULT_BOLD_TEXT_FONT_WEIGHT, DEFAULT_TITLE_FONT_SIZE } from "@styles/common-styles";
import { MainPageLayout } from "@components/page-layout/main-page-layout";
import { sdbCompanyIdsSelector, subscriptionStatusSelector } from "@store/sdb-company/sdb-company-selector";
import { Button, ChevronLeftIcon, FontWeights, Link, LoadingScreen, Text } from "@flat-ui";
import { useAppSelector } from "@store/store-helper";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { PlaceholderWrapper } from "@components/common/placeholder-wrapper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useSubscriptionExpiredPage } from "@pages/subscription-expired/use-subscription-expired-page";
import { useEffect } from "react";
import { assert } from "@faro-lotv/foundation";
import { runtimeConfig } from "@src/runtime-config";

/** Subscription expired page */
export function SubscriptionExpiredPage() {
  const $ = _c(41);
  const {
    trackEvent
  } = useTrackEvent();
  const sdbCompanyIds = useAppSelector(sdbCompanyIdsSelector);
  const companyId = useCompanyId();
  const subscriptionStatus = useAppSelector(subscriptionStatusSelector);
  const {
    canViewProductDetails
  } = useHasUserValidRoleCompanyLevel();
  const {
    navigateToRoot,
    navigateToProjects
  } = useAppNavigation();
  let t0;
  let t1;
  if ($[0] !== companyId || $[1] !== navigateToProjects || $[2] !== subscriptionStatus) {
    t0 = () => {
      if (subscriptionStatus === "subscription-active" || subscriptionStatus === "trial-active") {
        navigateToProjects({
          companyId
        });
      }
    };
    t1 = [navigateToProjects, subscriptionStatus, companyId];
    $[0] = companyId;
    $[1] = navigateToProjects;
    $[2] = subscriptionStatus;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  assert(subscriptionStatus === "subscription-ended" || subscriptionStatus === "trial-ended", `Invalid status: ${subscriptionStatus}. The code should not reach here.`);
  let t2;
  if ($[5] !== canViewProductDetails || $[6] !== subscriptionStatus) {
    t2 = {
      canViewProductDetails,
      subscriptionStatus
    };
    $[5] = canViewProductDetails;
    $[6] = subscriptionStatus;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    canApplyRetentionOffer,
    subtitle,
    retentionOfferDuration,
    onRetentionRequested,
    isLoading
  } = useSubscriptionExpiredPage(t2);
  if (isLoading) {
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <LoadingScreen />;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      px: 2,
      minHeight: "calc(80vh - 52px)",
      overflow: "hidden",
      mb: "60px",
      alignItems: "center",
      justifyContent: "space-between",
      textAlign: "center"
    };
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Box component="img" src={AccessDeniedIcon} sx={{
      width: "100%",
      maxWidth: "565px",
      mb: "32px"
    }} />;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      color: "text.secondary",
      mb: "20px",
      fontSize: "38px"
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = subscriptionStatus === "subscription-ended" ? "Subscription expired" : "Trial expired";
  let t7;
  if ($[12] !== t6) {
    t7 = <Text variant="heading20" sx={t5}>{t6}</Text>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      color: "text.secondary",
      maxWidth: "880px",
      fontSize: "20px"
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== subtitle) {
    t9 = <Text variant="bodyL" sx={t8}>{subtitle}</Text>;
    $[15] = subtitle;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      my: "100px",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 5,
      textAlign: "center"
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== navigateToRoot || $[19] !== sdbCompanyIds.length) {
    t11 = sdbCompanyIds.length > 1 && <FaroTextButton startIcon={<ChevronLeftIcon sx={{
      width: "20px",
      height: "20px"
    }} />} onClick={navigateToRoot} sx={{
      fontSize: "16px",
      fontWeight: FontWeights.SemiBold
    }}>Back to Workspaces</FaroTextButton>;
    $[18] = navigateToRoot;
    $[19] = sdbCompanyIds.length;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = canViewProductDetails && canApplyRetentionOffer && !!retentionOfferDuration;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      width: "162px",
      height: "24px",
      fontSize: DEFAULT_TITLE_FONT_SIZE,
      fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT,
      boxSizing: "content-box"
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== onRetentionRequested || $[23] !== retentionOfferDuration) {
    t14 = <Button sx={t13} onClick={onRetentionRequested}>Continue {retentionOfferDuration}-day trial</Button>;
    $[22] = onRetentionRequested;
    $[23] = retentionOfferDuration;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t12 || $[26] !== t14) {
    t15 = <PlaceholderWrapper isVisible={t12} shouldPreserveWidth={false}>{t14}</PlaceholderWrapper>;
    $[25] = t12;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t11 || $[29] !== t15) {
    t16 = <Stack direction="row" sx={t10}>{t11}{t15}</Stack>;
    $[28] = t11;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== trackEvent) {
    t17 = <Text variant="bodyM">Need help? Please contact{" "}<Link component="button" onClick={() => {
        contactSupport({
          location: ContactSupportLocations.subscriptionExpiredPage,
          trackEvent
        });
      }} aria-label="Contact support">{runtimeConfig.mailSupportAddress}</Link>{" "}</Text>;
    $[31] = trackEvent;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== canViewProductDetails || $[34] !== t17) {
    t18 = <PlaceholderWrapper isVisible={canViewProductDetails}>{t17}</PlaceholderWrapper>;
    $[33] = canViewProductDetails;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t16 || $[37] !== t18 || $[38] !== t7 || $[39] !== t9) {
    t19 = <MainPageLayout shouldShowSidebar={false} shouldUseFullBodyLayout={true} shouldShowLogo={true} shouldShowUserMenu={true} shouldShowProgressOverviewMenu={false}><Stack sx={t3} spacing={3}>{t4}{t7}{t9}{t16}{t18}</Stack></MainPageLayout>;
    $[36] = t16;
    $[37] = t18;
    $[38] = t7;
    $[39] = t9;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  return t19;
}